/**
 * Pure rules for signing in by name. The component (NameLoginDialog) is only the
 * wiring; what can be decided without a screen is decided here, so it can be tested.
 */

import { ApiError, type User } from '@/lib/api';

/** "Mario De Luca" -> "mario.de.luca": the same rule as the server and the app. */
export function slugName(...parts: string[]): string {
  return parts
    .join(' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '');
}

/** What the server accepts as a login name: lowercase letters and digits, dot-separated. */
export function isValidLoginName(value: string): boolean {
  return /^[a-z0-9]+(\.[a-z0-9]+)*$/.test(value) && value.length <= 60;
}

/** What the office typed, tidied: surrounding spaces and capitals do not matter. */
export function tidyLoginName(value: string): string {
  return value.trim().toLowerCase();
}

/** The server could not derive a login name from the person's name (e.g. Arabic script). */
export function needsLoginName(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422 && error.details?.code === 'LOGIN_NAME_REQUIRED';
}

/**
 * Will the employee get in by typing their first name and surname? Not when the office
 * typed a different name, or when the plain one was already taken and got a number.
 */
export function typedAsName(user: Pick<User, 'firstName' | 'lastName'>, loginName: string | null): boolean {
  return !loginName || loginName === slugName(user.firstName, user.lastName);
}

export interface SignInActions {
  /** Offer "Create name login". */
  create: boolean;
  /** Offer "Reset password". */
  reset: boolean;
}

/**
 * Which buttons the office sees for one person.
 *
 * Creating is for people without the portal and without a name login yet (portal users
 * keep their email flows). A reset needs an existing sign-in, and is never for yourself.
 */
export function signInActions(user: User, isSelf: boolean): SignInActions {
  if (user.status === 'archived') return { create: false, reset: false };
  const hasSignIn = user.signIn ? user.signIn !== 'none' : Boolean(user.loginName || user.email);
  return {
    create: !user.portalAccess && user.role !== 'admin' && !user.loginName,
    reset: hasSignIn && !isSelf,
  };
}

/** Copy to the clipboard; falls back to a hidden textarea where the API is unavailable. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Blocked (insecure origin, permissions): try the older route below.
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}
